import type { ApiClient } from '@classgo/app-kit';
import type {
  GenerateReportCardsInput,
  ReportCardBatchView,
  ReportCardDownload,
  ReportCardList,
} from '@classgo/shared';
import { queryOptions } from '@tanstack/react-query';

/**
 * The boletines' writes and the one read that is not cached (#116 over the API of #107): the
 * batch of a group and period (`grades:publish`), and the short-lived link to one student's PDF
 * (`grades:read`), which the screen fetches right away, so caching it would only hand out an
 * expired URL.
 *
 * @param {ApiClient} api -- The client.
 * @returns The calls.
 */
export const reportCardsApi = (api: ApiClient) => ({
  generate: (body: GenerateReportCardsInput) =>
    api.request<ReportCardBatchView>('/school/report-cards/generate', { method: 'POST', body }),
  download: (studentId: string, periodId: string) =>
    api.request<ReportCardDownload>(`/students/${studentId}/report-cards/${periodId}`),
});

/**
 * The boletines' reads: a batch, polled every two seconds while the worker has not finished, and
 * a student's periods of the year with how many subjects are published and when the PDF was
 * generated.
 */
export const reportCardQueries = {
  batch: (api: ApiClient, id: string) =>
    queryOptions({
      queryKey: ['school', 'report-card-batches', id],
      queryFn: () => api.request<ReportCardBatchView>(`/school/report-cards/batches/${id}`),
      enabled: id !== '',
      refetchInterval: (query) => {
        const status = query.state.data?.status;
        return status === 'queued' || status === 'running' ? 2000 : false;
      },
    }),
  ofStudent: (api: ApiClient, studentId: string) =>
    queryOptions({
      queryKey: ['school', 'report-cards', studentId],
      queryFn: () => api.request<ReportCardList>(`/students/${studentId}/report-cards`),
      enabled: studentId !== '',
    }),
};
